import {runAsync, sleep} from "@prosay/common"
import {KeyboardSpacer, TextInput, useSnackbarMessage} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import React, {useCallback, useMemo, useState} from "react"
import {
    ImageBackground,
    StyleProp,
    StyleSheet,
    View,
    ViewStyle,
} from "react-native"
import {Banner, Button, IconButton, Text} from "react-native-paper"
import {Actions} from "react-native-router-flux"
import {Field, Form} from "../../components"
import {Icons} from "../../constants"
import {
    CurrentUserDocument,
    RegisterOutput,
    useRegisterLawyerMutation,
    useRegisterMutation,
    useSendNewPhoneNumberTokenMutation,
    useSubmitPhoneNumberTokenMutation,
} from "../../graphql"
import {login} from "../../util"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    confirmPhoneNumberRow: {
        backgroundColor: "#ffffff",
        flex: 1,
        flexDirection: "row",
    },
    container: {
        flex: 1,
    },

    formContainer: {},

    imageBackground: {
        flex: 1,
        height: "100%",
        width: "100%",
    },

    phoneNumberInputRow: {
        backgroundColor: "#ffffff",
        flex: 1,
        flexDirection: "row",
    },
    phoneNumberItem: {
        // marginLeft: 0,
        // marginRight: 0,
        backgroundColor: "#fff",
    },
    phoneNumberItemContainer: {
        flex: 1,
    },
    registerContainer: {
        alignItems: "center",
        flex: 1,
        justifyContent: "center",
    },

    registerText: {
        color: "#0071d4",
        fontSize: 50,
        textShadowColor: "#000",
        textShadowOffset: {
            height: 50,
            width: 50,
        },
        textShadowRadius: 50,
    },

    userTypeActiveButton: {},
    userTypeButton: {
        flex: 1,
    },
    userTypeContainer: {
        backgroundColor: "#fff",
        flexDirection: "row",
    },
    userTypeNotActiveButton: {},
})

export interface ConfirmPhoneNumberProps {
    onSuccess: (token: string) => void | Promise<void>
}
export const ConfirmPhoneNumber: React.FC<ConfirmPhoneNumberProps> = ({
    onSuccess,
}) => {
    const mutation = useSubmitPhoneNumberTokenMutation({
        awaitRefetchQueries: true,
        onCompleted: async ({submitPhoneNumberToken}) => {
            if (!submitPhoneNumberToken) {
                return
            }
            await Promise.resolve(onSuccess(submitPhoneNumberToken))
        },
        refetchQueries: CurrentUserDocument,
    })

    const [
        sendNewPhoneNumberToken,
        {error},
    ] = useSendNewPhoneNumberTokenMutation()
    const onPress = useCallback(
        () =>
            runAsync(async () => {
                await sendNewPhoneNumberToken()
            }),
        [sendNewPhoneNumberToken],
    )

    return (
        <Form
            mutation={mutation}
            initialValues={{token: ""}}
            additionalErrors={error?.graphQLErrors}>
            <Field mutation={mutation} name="token">
                {({
                    field: {error, onBlur, onChangeText, value},
                    form: {submitForm},
                }) => (
                    <View style={styles.confirmPhoneNumberRow}>
                        <TextInput
                            mode="outlined"
                            containerStyle={styles.phoneNumberItemContainer}
                            style={styles.phoneNumberItem}
                            placeholder={translate(
                                "ui.screen.authenticate.tokenPlaceholder",
                            )}
                            onSubmitEditing={submitForm}
                            {...{error, onBlur, onChangeText, value}}
                        />

                        <Button>
                            <IconButton icon="refresh" onPress={onPress} />
                        </Button>

                        <Button>
                            <IconButton
                                icon="send"
                                onPress={() => void submitForm()}
                            />
                        </Button>
                    </View>
                )}
            </Field>
        </Form>
    )
}

export interface RegisterClientProps {
    disabled?: boolean
    onSuccess: (output: RegisterOutput) => Promise<void>
}
export const RegisterClient: React.FC<RegisterClientProps> = ({
    disabled = false,
    onSuccess,
}) => {
    const mutation = useRegisterMutation({
        awaitRefetchQueries: true,
        onCompleted: async ({register}) => {
            if (!register) {
                logger.warn`Register output is falsy. Please check errors.`
                return
            }

            await onSuccess(register)
        },
        refetchQueries: CurrentUserDocument,
    })

    return (
        <Form mutation={mutation} initialValues={{phoneNumber: ""}}>
            <Field mutation={mutation} name="phoneNumber">
                {({
                    field: {error, onBlur, onChangeText, value},
                    form: {submitForm},
                }) => (
                    <View style={styles.phoneNumberInputRow}>
                        <TextInput
                            mode="outlined"
                            containerStyle={styles.phoneNumberItemContainer}
                            style={styles.phoneNumberItem}
                            placeholder={translate(
                                "ui.screen.authenticate.phoneNumberPlaceholder",
                            )}
                            onSubmitEditing={submitForm}
                            {...{disabled, error, onBlur, onChangeText, value}}
                        />

                        {!disabled && (
                            <Button>
                                <IconButton
                                    icon="send"
                                    onPress={() => void submitForm()}
                                />
                            </Button>
                        )}
                    </View>
                )}
            </Field>
        </Form>
    )
}

export interface RegisterLawyerProps {
    disabled?: boolean
    onSuccess: (output: RegisterOutput) => Promise<void>
}
export const RegisterLawyer: React.FC<RegisterLawyerProps> = ({
    disabled = false,
    onSuccess,
}) => {
    const mutation = useRegisterLawyerMutation({
        awaitRefetchQueries: true,
        onCompleted: async ({registerLawyer}) => {
            if (!registerLawyer) {
                logger.warn`Register output is falsy. Please check errors.`
                return
            }

            await onSuccess(registerLawyer)
        },
        refetchQueries: CurrentUserDocument,
    })

    return (
        <Form
            mutation={mutation}
            initialValues={{inviteCode: "", phoneNumber: ""}}>
            <Field mutation={mutation} name="phoneNumber">
                {({field: {error, onBlur, onChangeText, value}}) => (
                    <TextInput
                        mode="outlined"
                        style={styles.phoneNumberItem}
                        placeholder={translate(
                            "ui.screen.authenticate.phoneNumberPlaceholder",
                        )}
                        keyboardType="phone-pad"
                        {...{disabled, error, onBlur, onChangeText, value}}
                    />
                )}
            </Field>
            <Field mutation={mutation} name="inviteCode">
                {({
                    field: {error, onBlur, onChangeText, value},
                    form: {submitForm},
                }) => (
                    <View style={styles.phoneNumberInputRow}>
                        <TextInput
                            mode="outlined"
                            containerStyle={styles.phoneNumberItemContainer}
                            style={styles.phoneNumberItem}
                            placeholder={translate(
                                "ui.screen.authenticate.invitePlaceholder",
                            )}
                            keyboardType="phone-pad"
                            onSubmitEditing={submitForm}
                            {...{disabled, error, onBlur, onChangeText, value}}
                        />

                        {!disabled && (
                            <Button>
                                <IconButton
                                    icon="send"
                                    onPress={() => void submitForm()}
                                />
                            </Button>
                        )}
                    </View>
                )}
            </Field>
        </Form>
    )
}

type AccountType = "Client" | "Lawyer"
export interface AccountSelectionButtonProps {
    accountType: AccountType
    desiredType: AccountType
    setAccountType: (type: AccountType) => void
    style?: StyleProp<ViewStyle>
}
export const AccountSelectionButton: React.FC<AccountSelectionButtonProps> = ({
    accountType,
    desiredType,
    setAccountType,
    style,
}) => {
    const icon = useMemo(
        () => (desiredType === "Client" ? Icons.client : Icons.lawyer),
        [desiredType],
    )
    const mode = useMemo(
        () => (accountType === desiredType ? "contained" : "outlined"),
        [accountType, desiredType],
    )
    const onPress = useCallback(() => setAccountType(desiredType), [
        setAccountType,
        desiredType,
    ])

    return (
        <Button
            style={[
                styles.userTypeButton,
                accountType === desiredType
                    ? styles.userTypeActiveButton
                    : styles.userTypeNotActiveButton,
                style,
            ]}
            {...{icon, mode, onPress}}>
            {translate(
                desiredType === "Client" ? "user.client" : "user.lawyer",
            )}
        </Button>
    )
}

export interface AuthenticationFormProps {
    onSuccessfulConfirmation: (jwtToken: string) => Promise<void>
    onSuccessfulRegister: (data: RegisterOutput) => Promise<void>
    unverified: boolean
}
export const AuthenticationForm: React.FC<AuthenticationFormProps> = ({
    onSuccessfulConfirmation,
    onSuccessfulRegister,
    unverified,
}) => {
    const [accountType, setAccountType] = useState<"Client" | "Lawyer">(
        "Client",
    )
    const [registered, setRegistered] = useState(unverified)
    const onSuccessfulRegister_ = useCallback(
        async (register: RegisterOutput) => {
            await onSuccessfulRegister(register)
            setRegistered(true)
        },
        [onSuccessfulRegister, setRegistered],
    )

    return (
        <View style={styles.formContainer}>
            <View style={styles.userTypeContainer}>
                <AccountSelectionButton
                    style={{
                        borderBottomRightRadius: 0,
                        borderTopRightRadius: 0,
                    }}
                    desiredType="Client"
                    {...{accountType, setAccountType}}
                />
                <AccountSelectionButton
                    style={{
                        borderBottomLeftRadius: 0,
                        borderTopLeftRadius: 0,
                    }}
                    desiredType="Lawyer"
                    {...{accountType, setAccountType}}
                />
            </View>

            {accountType === "Lawyer" ? (
                <RegisterLawyer
                    onSuccess={onSuccessfulRegister_}
                    disabled={unverified || registered}
                />
            ) : (
                <RegisterClient
                    onSuccess={onSuccessfulRegister_}
                    disabled={unverified || registered}
                />
            )}
            {registered && (
                <ConfirmPhoneNumber onSuccess={onSuccessfulConfirmation} />
            )}
        </View>
    )
}

export interface AuthenticateVerificationInformationBannerProps {
    unverified: boolean
}
export const VerificationBanner: React.FC<AuthenticateVerificationInformationBannerProps> = ({
    unverified,
}) => {
    const [visible, setVisible] = useState(true)
    const onPress = useCallback(() => setVisible(false), [setVisible])

    if (!unverified) {
        return null
    }

    return (
        <Banner
            visible={visible}
            actions={[
                {
                    label: translate("ui.banner.close"),
                    onPress,
                },
            ]}>
            {translate("ui.screen.authenticate.verficationBanner.text")}
        </Banner>
    )
}

// TODO: When we login for the first time while having something in our local storage, it does not redirect us to the home page.

export interface AuthenticateProps {
    unverified?: boolean
}
export const Authenticate: React.FC<AuthenticateProps> = ({
    unverified = false,
}) => {
    const [sendNewPhoneNumberToken] = useSendNewPhoneNumberTokenMutation()
    const sendMessage = useSnackbarMessage()
    const onSuccessfulRegister = useCallback(
        async ({id, jwtToken, refreshToken}: RegisterOutput) => {
            await login({id, jwtToken, refreshToken})
            await sendNewPhoneNumberToken()
        },
        [sendNewPhoneNumberToken],
    )

    const onSuccessfulConfirmation = useCallback(
        async (jwtToken: string) => {
            await login({jwtToken})
            sendMessage(
                translate(
                    "ui.screen.authenticate.successfullyRegisteredMessage",
                ),
            )
            await sleep(250)
            Actions.reset("authenticating")
        },
        [sendMessage],
    )

    return (
        <View style={styles.container}>
            <ImageBackground
                style={styles.imageBackground}
                source={{
                    uri:
                        "https://storage.needpix.com/rsynced_images/blue-paint-blend-background.jpg",
                }}>
                <VerificationBanner unverified={unverified} />

                <View style={styles.registerContainer}>
                    <Text style={styles.registerText}>
                        {translate("ui.screen.authenticate.registerText")}
                    </Text>
                </View>

                <AuthenticationForm
                    unverified={unverified}
                    onSuccessfulRegister={onSuccessfulRegister}
                    onSuccessfulConfirmation={onSuccessfulConfirmation}
                />
            </ImageBackground>
            <KeyboardSpacer />
        </View>
    )
}
